import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import {
  PatchRevisionDto,
  RevisionDescriptionSource,
  RevisionListItemDto,
} from 'services/api/api-client';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { Field } from 'components/uikit/Field';
import { FormError } from 'components/uikit/FormError';
import { Input } from 'components/uikit/inputs/Input';
import { Loading } from 'components/uikit/suspense/Loading';
import { HookFormDropDownInput } from 'components/uikit/inputs/dropdown/HookFormDropDownInput';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { QueryFactory } from 'services/api';
import { emptyArray } from 'helpers/empty-array';
import HintIcon from 'assets/icons/hint.svg?react';
import styles from './RevisionModal.module.scss';
import baseModalStyles from 'components/uikit/modal/Modal.module.scss';
import { useModal } from 'components/uikit/modal/useModal';
import {
  PillButton,
  PillColor,
} from 'components/uikit/buttons/pill/PillButton';
import { BrightTooltip } from 'components/tooltips/BrightTooltip';
import { useVariablesContext } from '../../../variables/variablesContext/VariablesContext';
import clsx from 'clsx';
import dropdownStyles from 'components/uikit/DropdownSelector.module.scss';

export enum RevisionModalMode {
  Add = 'add',
  Edit = 'edit',
}

export type RevisionModalProps = {
  onClose: () => void;
  isOpen: boolean;
  isBlocking: boolean;
  projectId: number;
} & (
  | {
      mode: RevisionModalMode.Add;
      onRevisionCreated: (revisionId: number) => void;
    }
  | {
      mode: RevisionModalMode.Edit;
      revision: RevisionListItemDto;
      basedOnRevisionVersion: string | undefined;
    }
);

type RevisionFormValues = {
  version: string;
  release: string;
  basedOnRevisionId: number | null;
  descriptionSource: RevisionDescriptionSource;
};

export const RevisionModal: React.FC<RevisionModalProps> = (props) => {
  const i18n = useScopedTranslation('Component.RevisionModal');
  const modalTranslation = useScopedTranslation('Component.CommonModals');
  const queryClient = useQueryClient();
  const confirmModal = useModal();
  const { refreshVariables } = useVariablesContext();

  const isAddMode = props.mode === RevisionModalMode.Add;
  const editedRevision =
    props.mode === RevisionModalMode.Edit ? props.revision : undefined;

  const revisionsQuery = QueryFactory.RevisionQuery.useSearchQuery(
    { projectId: props.projectId },
    { enabled: isAddMode && props.isOpen },
  );
  const revisions: RevisionListItemDto[] =
    revisionsQuery.data?.data ?? emptyArray();

  const revisionQuery = QueryFactory.RevisionQuery.useGetQuery(
    editedRevision?.id ?? 0,
    { enabled: !!editedRevision && props.isOpen },
  );

  const onModalClose = () => {
    form.reset();
    form.clearErrors();
    props.onClose();
  };

  const defaultValues = useMemo<RevisionFormValues>(
    () =>
      editedRevision
        ? {
            version: editedRevision.version,
            release: editedRevision.release ?? '',
            basedOnRevisionId: editedRevision.basedOnRevisionId,
            descriptionSource: editedRevision.descriptionSource,
          }
        : {
            version: '',
            release: '',
            basedOnRevisionId: revisions.length
              ? revisions[revisions.length - 1].id
              : null,
            descriptionSource: RevisionDescriptionSource.Revision,
          },
    [editedRevision, revisions],
  );

  const form = useAdvancedForm<RevisionFormValues>(
    useCallback(
      async (data) => {
        const release = data.release?.trim() || null;

        if (props.mode === RevisionModalMode.Add) {
          const createdRevision = await QueryFactory.RevisionClient.create({
            version: data.version,
            projectId: props.projectId,
            release: release,
            basedOnRevisionId: data.basedOnRevisionId,
            descriptionSource: data.descriptionSource,
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.RevisionQuery.searchQueryKey(),
          });

          props.onRevisionCreated(createdRevision.id);
          onModalClose();
          return;
        }

        const revision = props.revision;
        const dto: PatchRevisionDto = {};
        if (data.version !== revision.version) dto.version = data.version;
        if (release !== revision.release) dto.release = release;
        if (data.descriptionSource !== revision.descriptionSource)
          dto.descriptionSource = data.descriptionSource;

        if (Object.keys(dto).length === 0) {
          onModalClose();
          return;
        }

        const isRevisionExported = revisionQuery.data?.isExported;
        let confirmed = false;

        if (isRevisionExported) {
          confirmed = await confirmModal.showConfirm({
            title: modalTranslation.t('confirm_change'),
            text: modalTranslation.t('confirm_change_message'),
          });
        }

        if (confirmed || !isRevisionExported) {
          await QueryFactory.RevisionClient.patch(revision.id, dto);
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: QueryFactory.RevisionQuery.searchQueryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: QueryFactory.RevisionQuery.getQueryKey(revision.id),
            }),
            queryClient.invalidateQueries({
              queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
            }),
            refreshVariables(),
          ]);
        }

        onModalClose();
      },
      [props, revisionQuery.data],
    ),
    { defaultValues },
  );

  const release = form.watch('release');
  const version = form.watch('version');
  const descriptionSource = form.watch('descriptionSource');
  const autoDescriptionPreview =
    descriptionSource === RevisionDescriptionSource.Release
      ? i18n.t('description_release', { release })
      : i18n.t('description_revision', { revision: version });

  useEffect(() => {
    // If the release field is empty and the description source is set to Release,
    // switch the description source to Revision.
    if (!release && descriptionSource === RevisionDescriptionSource.Release)
      form.setValue('descriptionSource', RevisionDescriptionSource.Revision);
  }, [form, release, descriptionSource]);

  useEffect(() => {
    if (props.isOpen) setTimeout(() => form.setFocus('version'));
  }, [props.isOpen]);

  const latestRevision = revisions[0];

  return (
    <CustomModal
      title={i18n.t(isAddMode ? 'add_title' : 'edit_title')}
      isOpen={props.isOpen}
      onClose={onModalClose}
      isBlocking={props.isBlocking}
    >
      <Loading
        loading={
          form.formState.isSubmitting ||
          revisionsQuery.isLoading ||
          revisionQuery.isLoading
        }
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div className={styles.body}>
            <div className={styles.block}>
              <div className={styles.row}>
                <Field title={i18n.t('revision_field')}>
                  <Input
                    autoFocus={true}
                    {...form.register('version', { ...requiredRule() })}
                    errorText={form.formState.errors.version?.message}
                  />
                </Field>
                <Field title={i18n.t('release_field')}>
                  <Input
                    {...form.register('release')}
                    errorText={form.formState.errors.release?.message}
                  />
                </Field>
              </div>
              <Field
                title={i18n.t('based_on_field')}
                childrenWrapperClassName={styles.basedOnField}
              >
                <HookFormDropDownInput
                  disabled={props.mode !== RevisionModalMode.Add}
                  name={'basedOnRevisionId'}
                  control={form.control}
                  options={revisions}
                  getOptionLabel={(x) => x.version}
                  idFunction={'id'}
                  errorText={form.formState.errors.basedOnRevisionId?.message}
                  useVirtualization={false}
                  enableSearch={true}
                  classes={dropdownClasses}
                  required={true}
                  defaultValue={latestRevision}
                />
              </Field>
            </div>

            <div className={styles.block}>
              <Field
                title={i18n.t('description_title')}
                hint={{
                  title: i18n.t('description_title_tooltip'),
                  icon: <HintIcon />,
                }}
                titleClassName={styles.descriptionTitle}
              />
              <Field
                title={i18n.t('DescriptionSourceField.title')}
                className={styles.autoDescriptionSource}
              >
                <div className={styles.descriptionSourcePills}>
                  <PillButton
                    color={PillColor.Accent}
                    title={i18n.t('DescriptionSourceField.Options.revision')}
                    selected={
                      descriptionSource === RevisionDescriptionSource.Revision
                    }
                    onClick={() => {
                      form.setValue(
                        'descriptionSource',
                        RevisionDescriptionSource.Revision,
                      );
                    }}
                  />
                  <BrightTooltip
                    placement={'top'}
                    disableHoverListener={!!release}
                    title={i18n.t(
                      'DescriptionSourceField.Options.release_disabled_tooltip',
                    )}
                  >
                    <span>
                      <PillButton
                        color={PillColor.Accent}
                        title={i18n.t('DescriptionSourceField.Options.release')}
                        selected={
                          descriptionSource ===
                          RevisionDescriptionSource.Release
                        }
                        disabled={!release}
                        onClick={() => {
                          form.setValue(
                            'descriptionSource',
                            RevisionDescriptionSource.Release,
                          );
                        }}
                      />
                    </span>
                  </BrightTooltip>
                </div>
                <FormError>
                  {form.formState.errors.descriptionSource?.message}
                </FormError>
              </Field>
              <Field title={i18n.t('auto_description_field')}>
                <Input
                  className={styles.autoDescription}
                  readOnly
                  value={autoDescriptionPreview ?? ''}
                  testId={'current-revision-description'}
                />
              </Field>
            </div>

            <FormError>{form.overallError}</FormError>
          </div>
          <div className={baseModalStyles.footer}>
            <Button
              className={styles.button}
              color={ButtonColor.Secondary}
              title={i18n.t('cancel_button')}
              onClick={async () => onModalClose()}
            />
            <Button
              className={styles.button}
              color={ButtonColor.Default}
              type={'submit'}
              title={i18n.t('save_button')}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};

const dropdownClasses = {
  option: clsx(dropdownStyles.selectorOption, styles.statusOption),
  paper: dropdownStyles.selectorPaper,
  popper: dropdownStyles.selectorPopper,
};
